import { Col, Row } from 'antd';
import dayjs from 'dayjs';
import { FC } from 'react';

import { Schemas } from '@api-client/generated/types';
import { IconEditUnderline } from '@assets';
import { legalList, taxPeriodsList } from '@constants';
import { Amount, CompanyDetailsInfoCell } from '@entities';
import { complianceCompanyUrl, getAddressLine } from '@utils';

import * as S from './styled';

type CompanyDetailsInfoProps = {
  id?: string;
  name?: string;
  referenceId?: string;
  bobReferenceId?: string;
  details?: Schemas.CompanyDetailsDto;
  /**
   * The company's legacy status, which decides whether the compliance link is rendered at all:
   * compliance mirrors MDS, and MDS's importer takes neither prospects nor closed companies.
   */
  status?: Schemas.AdminCompanyResponseDto['status'];
  /** Opens the BOB ID modal. */
  onEditBobId: () => void;
  /** Opens the accounting modal — VAT periodicity, financial years, bank details. */
  onEditAccounting: () => void;
};

const getCompanyAndLegalDetails = (name?: string, legalForm?: string) => {
  const fullLabel: string[] = [];

  if (name) {
    fullLabel.push(name);
  }

  if (legalForm) {
    const selectedLegal = legalList.filter(
      (item) => item.value === legalForm
    )[0];

    fullLabel.push(selectedLegal.label);
  }

  return fullLabel.join(', ');
};

const getTaxPeriodLabel = (period: string) => {
  const taxPeriod = taxPeriodsList.filter((item) => item.value === period)[0];

  return taxPeriod.label.toLowerCase();
};

const CompanyDetailsInfo: FC<CompanyDetailsInfoProps> = ({
  id,
  name,
  referenceId,
  bobReferenceId,
  details,
  status,
  onEditBobId,
  onEditAccounting,
}) => {
  // Two of this card's fields are still ours and have their own modals: the BOB50 dossier, and the
  // accounting block MDS has no columns for (VAT periodicity, financial years, bank details). All
  // the rest is client data owned by compliance, so the third action leaves the backoffice
  // entirely instead of opening a form — and only for a company that can actually be there:
  // compliance mirrors MDS, and MDS's importer takes neither prospects nor closed companies, so
  // the link 404'd for four fifths of the estate before this check.
  const complianceUrl = complianceCompanyUrl(id, status);

  return (
    <S.Container>
      <S.Actions>
        <S.EditButton type="button" onClick={onEditBobId}>
          <IconEditUnderline width={16} height={16} /> Edit BOB ID
        </S.EditButton>

        <S.EditButton type="button" onClick={onEditAccounting}>
          <IconEditUnderline width={16} height={16} /> Edit accounting
        </S.EditButton>

        {complianceUrl && (
          <S.Edit
            href={complianceUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Edit company data in Compliance"
            title="Opens this company in Compliance (VPN only)"
          >
            <IconEditUnderline width={16} height={16} /> Edit
          </S.Edit>
        )}
      </S.Actions>

      <Row gutter={[20, 0]}>
        <Col span={12}>
          <CompanyDetailsInfoCell
            label="Company name & legal form"
            value={getCompanyAndLegalDetails(name, details?.legalForm)}
            isVisible={name}
            copyText={name!}
          />

          <CompanyDetailsInfoCell
            label="Client reference"
            value={referenceId}
            isVisible={referenceId}
            copyText={referenceId!}
          />

          <CompanyDetailsInfoCell
            label="BOB50 Dossier"
            value={bobReferenceId}
            isVisible={bobReferenceId}
            copyText={bobReferenceId!}
          />

          <CompanyDetailsInfoCell
            label="Business license"
            value={
              <>
                <span>{details?.businessLicenseNumber}</span>
                <span>{details?.businessLicenceActivityDescription}</span>
              </>
            }
            isVisible={
              details?.businessLicenseNumber ||
              details?.businessLicenceActivityDescription
            }
            copyText={`${details?.businessLicenseNumber} ${details?.businessLicenceActivityDescription}`.trim()}
          />

          <CompanyDetailsInfoCell
            label="Share capital"
            value={
              details?.shareCapital && (
                <Amount
                  amount={details?.shareCapital}
                  currencyCode="EUR"
                  withoutType
                />
              )
            }
            isVisible={details?.shareCapital}
            copyText={`€${details?.shareCapital}`}
          />

          <CompanyDetailsInfoCell
            label="Tax number"
            value={details?.taxNumber}
            isVisible={details?.taxNumber}
            copyText={details?.taxNumber}
          />

          <CompanyDetailsInfoCell
            label="VAT details"
            value={
              <>
                <span>{details?.vatId}</span>
                {details?.vatPeriodicity && (
                  <span>Paid {getTaxPeriodLabel(details?.vatPeriodicity)}</span>
                )}
              </>
            }
            isVisible={details?.vatId || details?.vatPeriodicity}
            copyText={`${details?.vatId} ${details?.vatPeriodicity ? `Paid ${getTaxPeriodLabel(details?.vatPeriodicity)}` : ''}`}
          />
        </Col>

        <Col span={12}>
          <CompanyDetailsInfoCell
            label="RCS information"
            value={
              <>
                <span>{details?.rscNumber}</span>
                <span>
                  {dayjs(details?.incorporationDate).format('DD.MM.YYYY')}
                </span>
              </>
            }
            isVisible={details?.rscNumber || details?.incorporationDate}
            copyText={`${details?.rscNumber} ${dayjs(details?.incorporationDate).format('DD.MM.YYYY')}`}
          />

          <CompanyDetailsInfoCell
            label="Financial year"
            value={details?.financialYear}
            isVisible={details?.financialYear}
            copyText={details?.financialYear}
          />

          <CompanyDetailsInfoCell
            label="First financial year"
            value={details?.firstFinancialYear}
            isVisible={details?.firstFinancialYear}
            copyText={details?.firstFinancialYear}
          />

          <CompanyDetailsInfoCell
            label="Legal address"
            value={details?.address && getAddressLine(details.address)}
            isVisible={details?.address}
            copyText={details?.address ? getAddressLine(details.address) : ''}
          />

          <CompanyDetailsInfoCell
            label="Bank details"
            value={
              <>
                <span>{details?.bankData?.bankName}</span>
                <span>{details?.bankData?.iban}</span>
              </>
            }
            isVisible={details?.bankData?.bankName || details?.bankData?.iban}
            copyText={`${details?.bankData?.bankName} ${details?.bankData?.iban}`}
          />
        </Col>
      </Row>
    </S.Container>
  );
};

export default CompanyDetailsInfo;
